import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { classNames } from "../utils";
import Profile from "./Profile";
import ToggleChevron from "./ToggleChevron";
import { ReactComponent as FamaLogo } from "./../assets/logo.svg";
import { ReactComponent as FamaLogoFolded } from "./../assets/fama-logo-small.svg";
import packageInfo from "../../package.json";
import { useAppContext } from "../AppProviders";
import { useFamaFlags } from "../api/FamaFlags";
import { ForwardRefExoticComponent, SVGProps, RefAttributes } from "react";
import ReactTooltip from "react-tooltip";

export type IconType =
  | ((props: React.SVGProps<SVGSVGElement>) => JSX.Element)
  | ForwardRefExoticComponent<
      SVGProps<SVGSVGElement> & RefAttributes<SVGSVGElement>
    >;

export type NavItem = {
  name: string;
  href: string;
  icon: IconType;
  disabled?: boolean;
  current?: boolean;
};

function pathIsCurrentNav(item: NavItem, path: string): boolean {
  return path.startsWith(item.href);
}

const openStyle: string =
  "col-span-1 flex w-64 flex-col inset-y-0 p-6 bg-gray-50";
const closedStyle: string =
  "col-span-1 flex w-15 flex-col inset-y-0 p-6 bg-gray-50";

function Nav({ navigation = [] }: { navigation?: NavItem[] }) {
  const [navFolded, setNavFolded] = useState<boolean>(true);
  const { showVersionNumber } = useFamaFlags();
  const { messageLog, user } = useAppContext();

  function secretClick(event: React.MouseEvent<HTMLElement>) {
    if (event.shiftKey) {
      const element = document.createElement("a");
      let local = messageLog;
      local.push({ title: "user", message: user });
      const file = new Blob([JSON.stringify(messageLog, null, 2)], {
        type: "application/json",
      });
      element.href = URL.createObjectURL(file);
      element.download = `${new Date().toLocaleDateString()}-logs.json`;
      document.body.appendChild(element);
      element.click();
    }
  }

  const location = useLocation();

  return (
    <div
      className={classNames(
        navFolded ? closedStyle : openStyle,
        "print:hidden"
      )}
    >
      <div className="bg-white flex flex-col min-h-0 rounded-lg border shadow">
        <div className="flex items-center h-16 flex-shrink-0 px-4 col-start-1 col-span-4 justify-center">
          {!navFolded && (
            <Link className="flex  h-11" to="/landing">
              <FamaLogo />
            </Link>
          )}

          <div>
            <ToggleChevron
              onClick={() => {
                setNavFolded(!navFolded);
              }}
            ></ToggleChevron>
          </div>
        </div>
        {navFolded && (
          <div className="flex justify-center items-center">
            <Link className="flex  h-11" to="/landing">
              <FamaLogoFolded />
            </Link>
          </div>
        )}
        <div className="flex flex-col overflow-y-auto">
          <nav role="tabpanel" className="py-4 space-y-1">
            {navigation.map((item, index) => (
              <span key={index}>
                <NavRowWithTooltip
                  item={item}
                  location={location}
                  navFolded={navFolded}
                />
              </span>
            ))}
            <div className="pt-60">
              <Profile fullDisplay={!navFolded} />
            </div>
          </nav>

          {showVersionNumber && (
            <span
              onClick={secretClick}
              className="flex justify-center text-xs text-gray-500"
            >
              {!navFolded && "Version:"} {packageInfo.version}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function NavRowWithTooltip({
  item,
  location,
  navFolded,
}: {
  item: NavItem;
  location: any;
  navFolded: boolean;
}) {
  return (
    <span key={item.name}>
      <Link
        key={item.name}
        to={item.href}
        onClick={(event) => {
          if (item.disabled) {
            event.preventDefault();
            return;
          }
        }}
        className={classNames(
          pathIsCurrentNav(item, location.pathname)
            ? "text-green-600 bg-green-50 border-l-4 border-green-600"
            : "text-gray-600 group-hover:text-green-600 hover:bg-green-50 border-l-4 border-transparent",
          "group flex items-center px-2 py-2 text-gray-600 text-sm font-medium",
          navFolded ? "justify-center" : "",
          item.disabled ? "cursor-default" : ""
        )}
        data-tip={item.name}
        data-for={"rt-" + item.href}
      >
        <item.icon
          className={classNames(
            pathIsCurrentNav(item, location.pathname)
              ? "text-green-600"
              : "text-gray-600 group-hover:text-green-600",
            "flex-shrink-0 h-6 w-6"
          )}
          aria-hidden="true"
        />
        {!navFolded && <span className="pl-2.5">{item.name}</span>}
      </Link>
      {navFolded && (
        <ReactTooltip
          className="hover:visible:opacity-1 w-36"
          effect="solid"
          id={"rt-" + item.href}
        />
      )}
    </span>
  );
}

export default Nav;
